import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Switch,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { fetchWithToken } from "../api/apiutils";
// import Paging from "./Paging";
import KtruSourcesFilter from "../features/activeusers/components/Ktru/KtruSourcesFilter";

const SOURCE_TYPE_LABELS = {
  marketplace: "Маркетплейс",
  retailer: "Ритейлер",
  wholesale: "Опт",
};

const SOURCE_TYPE_COLORS = {
  marketplace: "primary",
  retailer: "success",
  wholesale: "warning",
};

export const KtruSourcesPage = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ search: "", type: "", status: "" });

  const rowsPerPage = 10;

  const fetchData = useCallback(async (page, activeFilters) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page,
        pageLimit: rowsPerPage,
        ...(activeFilters.search && { ktruCode: activeFilters.search }),
        ...(activeFilters.type && { sourceType: activeFilters.type }),
        ...(activeFilters.status && { isActive: activeFilters.status }),
      });

      const response = await fetchWithToken(`api/ktru-sources?${params}`);

      const json = await response.json();
      setData(json || []);
      setTotalRecords(json.length || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, filters);
  }, [currentPage, filters, fetchData]);

  const totalPages = useMemo(
    () => Math.ceil(totalRecords / rowsPerPage),
    [totalRecords],
  );

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleToggle = useCallback(async (id, currentValue) => {
    try {
      const params = new URLSearchParams({ IsActive: !currentValue });
      const response = await fetchWithToken(
        `/api/ktru-sources/${id}/toggle-status?${params}`,
        { method: "PATCH" },
      );
      if (!response.ok) throw new Error("Ошибка при обновлении статуса");
      setData((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isActive: !currentValue } : item,
        ),
      );
      fetchData(currentPage, filters);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm("Удалить эту запись?")) return;
      try {
        const response = await fetchWithToken(`/api/ktru-sources/${id}`, {
          method: "DELETE",
        });
        if (!response.ok) throw new Error("Ошибка при удалении");
        fetchData(currentPage, filters);
      } catch (err) {
        console.error(err);
      }
    },
    [currentPage, filters, fetchData],
  );

  return (
    <Box sx={{ p: "40px" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Источники по КТРУ
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/ktru-sources/add")}
          sx={{
            backgroundColor: "#4298D7",
            "&:hover": { backgroundColor: "#3182c4" },
          }}
        >
          Добавить источник
        </Button>
      </Box>

      {/* Filters */}
      <KtruSourcesFilter onFilterChange={handleFilterChange} />

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">Ошибка: {error}</Typography>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>КТРУ код</TableCell>
                <TableCell>Название КТРУ</TableCell>
                <TableCell>Источник</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Приоритет</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Изменил</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length > 0 ? (
                data.map((item, index) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </TableCell>

                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {item.ktruCode || "—"}
                    </TableCell>

                    <TableCell>{item.ktruName || "—"}</TableCell>

                    <TableCell>
                      <a
                        href={item.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: "#4298D7",
                          fontWeight: 500,
                          textDecoration: "none",
                        }}
                      >
                        {item.sourceName || "—"}
                      </a>
                    </TableCell>

                    <TableCell>
                      {item.sourceType ? (
                        <Chip
                          label={
                            SOURCE_TYPE_LABELS[item.sourceType] ||
                            item.sourceType
                          }
                          color={
                            SOURCE_TYPE_COLORS[item.sourceType] || "default"
                          }
                          size="small"
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 26,
                          height: 26,
                          borderRadius: 1.5,
                          bgcolor: item.priority === 1 ? "#4298D7" : "#e5e7eb",
                          color: item.priority === 1 ? "#fff" : "#6b7280",
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {item.priority ?? "—"}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={item.isActive ? "Выключить" : "Включить"}>
                        <Switch
                          checked={!!item.isActive}
                          onChange={() => handleToggle(item.id, item.isActive)}
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#22c55e",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#22c55e",
                              },
                          }}
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        fontSize={12}
                      >
                        {item.updatedBy}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.updatedAt
                          ? new Date(item.updatedAt).toLocaleString("ru-RU", {
                              dateStyle: "short",
                              timeStyle: "short",
                              timeZone: "Asia/Almaty",
                            })
                          : "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                          onClick={() =>
                            navigate(`/ktru-sources/edit/${item.id}`)
                          }
                          sx={{ fontSize: 12, textTransform: "none" }}
                        >
                          Редактировать
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(item.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                    sx={{ py: 4, color: "text.secondary" }}
                  >
                    Нет данных
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {/* 
          <Paging
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          /> */}
        </TableContainer>
      )}
    </Box>
  );
};
